import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Search, File, Calendar, User, Folder, Upload } from 'lucide-react';
import { getFileIcon, getFileExtension } from '../utils/fileIcons';

const API = window.location.origin || 'http://localhost:8001';

const FileSelector = ({ onFileSelect, onNewFileUpload, onClose, uploading = false, uploadProgress = 0 }) => {
  const [activeTab, setActiveTab] = useState('new'); // 'new' or 'existing'
  const [existingFiles, setExistingFiles] = useState([]);
  const [filteredFiles, setFilteredFiles] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);

  useEffect(() => {
    if (activeTab === 'existing') {
      fetchExistingFiles();
    }
  }, [activeTab]);

  useEffect(() => {
    // Dosyaları s3Key'e göre grupla (aynı dosyanın birden fazla göreve atanması durumu)
    const groupedFiles = {};
    existingFiles.forEach(file => {
      const key = file.s3Key || file.id; // s3Key yoksa id kullan (eski base64 dosyalar için)
      
      if (!groupedFiles[key]) {
        groupedFiles[key] = {
          ...file,
          tasks: [{ taskId: file.taskId, taskTitle: file.taskTitle, projectName: file.projectName }]
        };
      } else {
        // Aynı s3Key'e sahip başka bir kayıt, task'ı ekle
        groupedFiles[key].tasks.push({ 
          taskId: file.taskId, 
          taskTitle: file.taskTitle,
          projectName: file.projectName 
        });
      }
    });
    
    const uniqueFiles = Object.values(groupedFiles);
    
    // Arama filtresi
    if (searchTerm) {
      const filtered = uniqueFiles.filter(file => 
        file.filename.toLowerCase().includes(searchTerm.toLowerCase()) ||
        file.tasks.some(task => 
          task.taskTitle?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          task.projectName?.toLowerCase().includes(searchTerm.toLowerCase())
        )
      );
      setFilteredFiles(filtered);
    } else {
      setFilteredFiles(uniqueFiles);
    }
  }, [searchTerm, existingFiles]);

  const fetchExistingFiles = async () => {
    setLoading(true);
    try {
      // Sayfalanmış endpoint'ten tüm dosyaları çek (selector için büyük limit)
      const response = await axios.get(`${API}/api/files?limit=500`);
      const filesData = response.data.files || response.data;
      setExistingFiles(Array.isArray(filesData) ? filesData : []);
    } catch (error) {
      console.error('Dosyalar yüklenirken hata:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileInputChange = (e) => {
    const file = e.target.files[0];
    if (file && onNewFileUpload) {
      onNewFileUpload(file);
    }
  };

  const handleExistingFileSelect = (file) => {
    setSelectedFile(file.id);
    if (onFileSelect) {
      onFileSelect(file);
    }
  };

  // Drag and Drop handlers
  const [isDragging, setIsDragging] = useState(false);

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      const file = files[0];
      if (onNewFileUpload) {
        onNewFileUpload(file);
      }
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('tr-TR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="flex flex-col h-full w-full">
      {/* Header - Sabit */}
      <div className="bg-gradient-to-r from-cyan-600 via-blue-600 to-cyan-700 px-4 sm:px-6 py-3 sm:py-4 flex-shrink-0 rounded-t-lg">
        <h3 className="text-lg sm:text-xl font-bold text-white flex items-center gap-2">
          <div className="w-7 h-7 sm:w-9 sm:h-9 bg-white/20 rounded-lg flex items-center justify-center flex-shrink-0">
            <File className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
          </div>
          Dosya Ekle
        </h3>
        <p className="text-xs text-cyan-100 mt-1 ml-9 sm:ml-11">Yeni dosya yükleyin veya mevcut dosyalardan seçin</p>
      </div>

      {/* Tabs - Sabit */}
      <div className="flex border-b-2 border-gray-200 bg-gradient-to-r from-gray-50 to-blue-50 flex-shrink-0">
        <button
          onClick={() => setActiveTab('new')}
          className={`flex-1 px-3 sm:px-6 py-3 sm:py-4 text-xs sm:text-sm font-bold transition-all duration-200 flex items-center justify-center gap-1 sm:gap-2 relative ${
            activeTab === 'new'
              ? 'text-cyan-700 bg-white shadow-lg'
              : 'text-gray-600 hover:text-cyan-600 hover:bg-white/50'
          }`}
        >
          {activeTab === 'new' && (
            <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-cyan-500 to-blue-500"></div>
          )}
          <div className={`w-6 h-6 sm:w-8 sm:h-8 rounded-lg flex items-center justify-center ${
            activeTab === 'new' ? 'bg-gradient-to-br from-cyan-500 to-blue-600' : 'bg-gray-200'
          }`}>
            <Upload className={`w-3 h-3 sm:w-4 sm:h-4 ${activeTab === 'new' ? 'text-white' : 'text-gray-600'}`} />
          </div>
          <span className="hidden sm:inline">Yeni Dosya Yükle</span>
          <span className="sm:hidden">Yeni</span>
        </button>
        <button
          onClick={() => setActiveTab('existing')}
          className={`flex-1 px-3 sm:px-6 py-3 sm:py-4 text-xs sm:text-sm font-bold transition-all duration-200 flex items-center justify-center gap-1 sm:gap-2 relative ${
            activeTab === 'existing'
              ? 'text-cyan-700 bg-white shadow-lg'
              : 'text-gray-600 hover:text-cyan-600 hover:bg-white/50'
          }`}
        >
          {activeTab === 'existing' && (
            <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-cyan-500 to-blue-500"></div>
          )}
          <div className={`w-6 h-6 sm:w-8 sm:h-8 rounded-lg flex items-center justify-center ${
            activeTab === 'existing' ? 'bg-gradient-to-br from-cyan-500 to-blue-600' : 'bg-gray-200'
          }`}>
            <Folder className={`w-3 h-3 sm:w-4 sm:h-4 ${activeTab === 'existing' ? 'text-white' : 'text-gray-600'}`} />
          </div>
          <span className="hidden sm:inline">Mevcut Dosyalardan Seç</span>
          <span className="sm:hidden">Mevcut</span>
        </button>
      </div>

      {/* Content - Scrollable */}
      <div className="flex-1 overflow-y-auto bg-gradient-to-br from-gray-50 to-blue-50/30 p-4 sm:p-6">
        {activeTab === 'new' ? (
          <div 
            className={`h-full border-4 border-dashed rounded-2xl p-4 sm:p-6 w-full flex flex-col items-center justify-center text-center bg-gradient-to-br from-cyan-50 via-blue-50 to-cyan-50 hover:shadow-2xl transition-all duration-300 group relative overflow-hidden ${
              isDragging 
                ? 'border-blue-500 bg-blue-100 scale-[1.02]' 
                : 'border-cyan-300 hover:border-blue-500'
            }`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            <div className="absolute inset-0 bg-gradient-to-r from-cyan-400/10 to-blue-400/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            <input
              type="file"
              id="file-upload"
              className="hidden"
              accept="*/*"
              onChange={handleFileInputChange}
            />
            {/* Upload Progress Overlay */}
            {uploading && (
              <div className="absolute inset-0 z-20 bg-white/95 backdrop-blur-sm flex flex-col items-center justify-center p-6 rounded-2xl">
                <div className="relative mb-6">
                  <div className="w-24 h-24 rounded-full bg-gradient-to-br from-cyan-100 to-blue-100 flex items-center justify-center animate-pulse">
                    <Upload className="w-12 h-12 text-cyan-600 animate-bounce" />
                  </div>
                  <div className="absolute -top-2 -right-2 w-8 h-8 bg-gradient-to-br from-cyan-500 to-blue-500 rounded-full flex items-center justify-center text-white text-xs font-bold">
                    {uploadProgress}%
                  </div>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Dosya Yükleniyor...</h3>
                <p className="text-sm text-gray-600 mb-4">Lütfen bekleyin, dosyanız yükleniyor</p>
                <div className="w-full max-w-md">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs text-gray-500">İlerleme</span>
                    <span className="text-sm font-bold text-cyan-600">{uploadProgress}%</span>
                  </div>
                  <div className="relative w-full h-3 bg-gray-200 rounded-full overflow-hidden shadow-inner">
                    <div 
                      className="absolute top-0 left-0 h-full bg-gradient-to-r from-cyan-500 via-blue-500 to-cyan-600 rounded-full transition-all duration-300 ease-out shadow-lg"
                      style={{ width: `${uploadProgress}%` }}
                    >
                      <div className="absolute inset-0 bg-white/30 animate-pulse"></div>
                    </div>
                  </div>
                  <div className="mt-3 text-center">
                    {uploadProgress < 30 && <span className="text-xs text-gray-500">📤 Dosya aktarılıyor...</span>}
                    {uploadProgress >= 30 && uploadProgress < 70 && <span className="text-xs text-blue-600">⏳ Yükleme devam ediyor...</span>}
                    {uploadProgress >= 70 && uploadProgress < 100 && <span className="text-xs text-cyan-600">🚀 Neredeyse tamamlandı...</span>}
                    {uploadProgress === 100 && <span className="text-xs text-green-600">✅ Tamamlanıyor...</span>}
                  </div>
                </div>
              </div>
            )}
            
            <label
              htmlFor="file-upload"
              className="cursor-pointer flex flex-col items-center relative z-10"
            >
              <div className="w-14 h-14 sm:w-20 sm:h-20 bg-gradient-to-br from-cyan-500 via-blue-500 to-cyan-600 rounded-2xl flex items-center justify-center mb-3 sm:mb-4 group-hover:scale-110 group-hover:rotate-3 transition-all duration-300 shadow-2xl">
                <Upload className="w-7 h-7 sm:w-10 sm:h-10 text-white" />
              </div>
              <span className={`text-sm sm:text-lg font-bold mb-1 sm:mb-2 transition-colors ${
                isDragging ? 'text-blue-700' : 'text-gray-800 group-hover:text-cyan-700'
              }`}>
                {isDragging ? 'Dosyayı buraya bırakın!' : 'Bilgisayarınızdan dosya seçin'}
              </span>
              <span className={`text-xs sm:text-sm mb-3 sm:mb-4 ${
                isDragging ? 'text-blue-600 font-semibold' : 'text-gray-600'
              }`}>
                {isDragging ? '👆 Dosyayı şimdi bırakın' : 'veya bu alana sürükleyip bırakın'}
              </span>
              <div className="inline-flex items-center gap-1.5 sm:gap-2 px-3 py-1.5 sm:py-2 bg-white/80 rounded-full border-2 border-cyan-200">
                <File className="w-3 h-3 text-cyan-600" />
                <span className="text-[10px] sm:text-xs font-medium text-cyan-700">Tüm dosya türleri</span>
              </div>
            </label>
          </div>
        ) : (
          <div className="space-y-5">
            {/* Search */}
            <div className="relative mb-3">
              <div className="absolute left-3 top-1/2 transform -translate-y-1/2 bg-gradient-to-r from-cyan-500 to-blue-500 rounded-lg p-1.5">
                <Search className="text-white w-3.5 h-3.5 sm:w-4 sm:h-4" />
              </div>
              <input
                type="text"
                placeholder="Dosya, görev veya proje ara..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-11 sm:pl-12 pr-3 py-2.5 sm:py-3 bg-white border-2 border-cyan-200 rounded-xl focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 transition-all text-xs sm:text-sm font-medium placeholder:text-gray-400"
              />
            </div>

            {/* File List */}
            <div className="space-y-2 sm:space-y-3">
              {loading ? (
                <div className="text-center py-10 sm:py-12 bg-gradient-to-br from-cyan-50 to-blue-50 rounded-xl">
                  <div className="inline-block animate-spin rounded-full h-8 w-8 sm:h-10 sm:w-10 border-3 border-cyan-200 border-t-cyan-600"></div>
                  <p className="text-cyan-700 font-semibold mt-3 text-xs sm:text-sm">Yükleniyor...</p>
                </div>
              ) : filteredFiles.length === 0 ? (
                <div className="text-center py-10 sm:py-12 bg-gradient-to-br from-gray-50 to-blue-50 rounded-xl border-2 border-dashed border-gray-300">
                  <div className="w-12 h-12 sm:w-16 sm:h-16 bg-gradient-to-br from-gray-100 to-gray-200 rounded-xl flex items-center justify-center mx-auto mb-3">
                    <File className="w-6 h-6 sm:w-8 sm:h-8 text-gray-400" />
                  </div>
                  <p className="text-gray-600 font-semibold text-sm sm:text-base">
                    {searchTerm ? 'Dosya bulunamadı' : 'Henüz dosya yüklenmemiş'}
                  </p>
                  <p className="text-gray-500 text-xs mt-1">
                    {searchTerm ? 'Farklı bir arama terimi deneyin' : 'Yeni dosya yükleyerek başlayın'}
                  </p>
                </div>
              ) : (
                filteredFiles.map((file) => {
                  const { Icon: FileIcon, color, bgColor, label } = getFileIcon(file.filename);
                  const extension = getFileExtension(file.filename);
                  
                  return (
                  <div
                    key={file.id}
                    onClick={() => handleExistingFileSelect(file)}
                    className={`p-2.5 sm:p-4 rounded-xl border-2 cursor-pointer transition-all duration-200 ${
                      selectedFile === file.id
                        ? 'border-cyan-500 bg-gradient-to-r from-cyan-50 via-blue-50 to-cyan-50 shadow-lg ring-2 ring-cyan-200'
                        : 'border-gray-200 bg-white hover:border-cyan-300 hover:bg-gradient-to-r hover:from-gray-50 hover:to-blue-50 hover:shadow-md'
                    }`}
                  >
                    <div className="flex items-start gap-2 sm:gap-3">
                      <div className="relative flex-shrink-0">
                        <div className={`w-9 h-9 sm:w-11 sm:h-11 rounded-lg flex items-center justify-center transition-all duration-200 ${
                          selectedFile === file.id 
                            ? 'bg-gradient-to-br from-cyan-500 to-blue-600 shadow-md' 
                            : `bg-gradient-to-br ${bgColor}`
                        }`}>
                          <FileIcon className={`w-4 h-4 sm:w-5 sm:h-5 ${selectedFile === file.id ? 'text-white' : color}`} />
                        </div>
                        {extension && (
                          <div className={`absolute -bottom-0.5 -right-0.5 text-[7px] sm:text-[8px] font-bold px-1 py-0.5 rounded ${
                            selectedFile === file.id 
                              ? 'bg-white text-cyan-600'
                              : 'bg-white text-gray-600 border border-gray-200'
                          }`}>
                            {extension.toUpperCase()}
                          </div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-col sm:flex-row sm:items-center gap-0.5 sm:gap-1.5 mb-1.5 sm:mb-2">
                          <div className={`font-bold truncate text-sm sm:text-base ${selectedFile === file.id ? 'text-cyan-700' : 'text-gray-900'}`}>
                            {file.filename}
                          </div>
                          {file.tasks && file.tasks.length > 1 && (
                            <span className="flex-shrink-0 text-[9px] sm:text-[10px] font-bold text-white bg-gradient-to-r from-orange-500 to-red-500 px-1.5 sm:px-2 py-0.5 rounded-full shadow-sm w-fit">
                              {file.tasks.length} görevde
                            </span>
                          )}
                        </div>
                        <div className="flex flex-wrap gap-1 sm:gap-1.5 text-[9px] sm:text-[10px] mb-1.5 sm:mb-2">
                          {file.tasks && file.tasks.slice(0, 1).map((task, idx) => (
                            <div key={idx} className="flex flex-wrap items-center gap-1">
                              {task.projectName && (
                                <div className="flex items-center gap-0.5 sm:gap-1 bg-blue-50 px-1.5 sm:px-2 py-0.5 sm:py-1 rounded border border-blue-200">
                                  <Folder className="w-2.5 h-2.5 sm:w-3 sm:h-3 text-blue-600 flex-shrink-0" />
                                  <span className="font-semibold text-blue-700 truncate max-w-[60px] sm:max-w-[100px]">{task.projectName}</span>
                                </div>
                              )}
                              {task.taskTitle && (
                                <div className="flex items-center gap-0.5 sm:gap-1 bg-cyan-50 px-1.5 sm:px-2 py-0.5 sm:py-1 rounded border border-cyan-200">
                                  <span className="truncate max-w-[80px] sm:max-w-[120px] text-cyan-600 font-medium">{task.taskTitle}</span>
                                </div>
                              )}
                            </div>
                          ))}
                          {file.tasks && file.tasks.length > 1 && (
                            <div className="bg-gray-100 px-1.5 sm:px-2 py-0.5 sm:py-1 rounded border border-gray-300 text-gray-700 font-semibold">
                              +{file.tasks.length - 1}
                            </div>
                          )}
                        </div>
                        <div className="flex flex-wrap gap-1.5 sm:gap-3 text-[9px] sm:text-[10px]">
                          <div className="flex items-center gap-0.5 sm:gap-1 text-gray-600">
                            <div className="w-4 h-4 sm:w-5 sm:h-5 bg-purple-100 rounded flex items-center justify-center">
                              <User className="w-2.5 h-2.5 sm:w-3 sm:h-3 text-purple-600" />
                            </div>
                            <span className="font-medium truncate max-w-[60px] sm:max-w-[80px]">{file.uploaderName}</span>
                          </div>
                          <div className="flex items-center gap-0.5 sm:gap-1 text-gray-600">
                            <div className="w-4 h-4 sm:w-5 sm:h-5 bg-green-100 rounded flex items-center justify-center">
                              <Calendar className="w-2.5 h-2.5 sm:w-3 sm:h-3 text-green-600" />
                            </div>
                            <span className="font-medium">{formatDate(file.createdAt)}</span>
                          </div>
                          <div className="flex items-center gap-0.5 sm:gap-1">
                            <div className="w-4 h-4 sm:w-5 sm:h-5 bg-orange-100 rounded flex items-center justify-center">
                              <File className="w-2.5 h-2.5 sm:w-3 sm:h-3 text-orange-600" />
                            </div>
                            <span className="font-bold text-gray-700">{formatFileSize(file.fileSize)}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  );
                })
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FileSelector;
